import * as CL from '@design-system/component-library';
import * as React from 'react';
import { FilterTag } from '../FilterTag/FilterTag';
import {
  ProductGridFilter,
  ProductGridFilterHeader,
  ProductGridFilterStatus,
  hasActiveFilters,
} from './ProductGridFilter.js';
import { ProductGridSearchCL } from './ProductGridSearch.js';
import { ProductGridSort } from './ProductGridSort.js';
import { searchMsg, t } from '../../common/i18n/index.js';
import type { Filters } from './ProductGridFilter.js';
import type { SortType } from './ProductGridSort.js';

interface ProductGridHeaderProps {
  setFilters: React.Dispatch<React.SetStateAction<Filters>>;
  setSearch: React.Dispatch<React.SetStateAction<string>>;
  setSort: React.Dispatch<React.SetStateAction<SortType>>;
  filters: Filters;
  search: string;
  sort: SortType;
}

export const ProductGridHeader = ({
  setSearch,
  search,
  filters,
  setFilters,
  setSort,
  sort,
}: ProductGridHeaderProps) => {
  const [isFilterExpanded, setIsFilterExpanded] = React.useState(false);
  const allFilters = Object.entries(filters).map(([subcategory, filter]) => ({
    subcategory,
    selectedOptions: filter.selectedOptions || [],
  }));

  const handleRemoveFilter = (subcategory: string, label: string) => {
    const removedFilterSubcategory = filters[subcategory as keyof Filters];
    const current: Filters = {
      ...filters,
      [subcategory]: {
        ...removedFilterSubcategory,
        selectedOptions: removedFilterSubcategory?.selectedOptions?.filter((option: string) => option !== label) || [],
      },
    };
    setFilters(current);
  };

  const selectedFilterCount = Object.values(filters).reduce(
    (count, filter) => count + filter.selectedOptions.length,
    0
  );

  return (
    <>
      <CL.Grid>
        <CL.GridRow className="of-product-grid-header">
          <CL.GridCol colWidthL={3} colWidthM={2} colWidthS={6} colWidthXS={4}>
            <ProductGridSearchCL placeholder={t.VQE6(searchMsg)} onChange={setSearch} value={search} />
          </CL.GridCol>
          {hasActiveFilters(filters) && (
            <>
              <CL.GridCol
                colWidthL={3}
                colWidthM={2}
                colWidthS={6}
                colWidthXS={4}
                className="of-product-grid__filter-header"
              >
                <ProductGridFilterHeader onChange={setIsFilterExpanded} value={isFilterExpanded} />
              </CL.GridCol>
              <CL.GridCol
                colWidthL={3}
                colWidthM={2}
                colWidthS={6}
                colWidthXS={4}
                className="of-product-grid__filter-status"
              >
                <ProductGridFilterStatus filters={filters} setFilters={setFilters} />
              </CL.GridCol>
            </>
          )}
          <CL.GridCol colWidthL={3} colWidthM={2} colWidthS={6} colWidthXS={4} className="of-product-grid__sort">
            <ProductGridSort onChange={setSort} selectedValue={sort} />
          </CL.GridCol>
        </CL.GridRow>
      </CL.Grid>

      {selectedFilterCount > 0 && (
        <CL.Grid key="selectedFilters" className="of-product-grid__filter-tags">
          <CL.GridRow>
            <div className="of-product-grid__filter-tags-container">
              {allFilters.map(filter =>
                filter.selectedOptions.map((selectedOption: string, index: number) => (
                  <FilterTag
                    subcategory={filter.subcategory}
                    label={selectedOption}
                    key={`${filter.subcategory}-${index}`}
                    onRemoveFilter={() => {
                      handleRemoveFilter(filter.subcategory, selectedOption);
                    }}
                  />
                ))
              )}
            </div>
          </CL.GridRow>
        </CL.Grid>
      )}
      {isFilterExpanded && (
        <div className="of-product-grid__filter-content">
          <ProductGridFilter filters={filters} onChange={setFilters} onClose={() => setIsFilterExpanded(false)} />
        </div>
      )}
    </>
  );
};
